import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Flex,
  Typography,
  Card,
  Table,
  Tag,
  Input,
  Select,
  Button,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip,
  message,
  Spin,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import InrOutlined from "../../general/components/common/InrOutlined";
import superAdminService from "../services/superAdminService";

const { Title, Text } = Typography;

function GlobalPaymentsView({ onSelectCompany }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");

  const fetchPayments = useCallback(async (period = periodFilter) => {
    setLoading(true);
    try {
      const res = await superAdminService.getAllPayments({
        period,
        status: statusFilter,
        method: methodFilter,
      });
      if (res?.success && Array.isArray(res.payments)) {
        setPayments(res.payments);
      }
    } catch (err) {
      console.error("Failed to load global payments:", err);
      message.error(err.message || "Failed to load payment transactions.");
    } finally {
      setLoading(false);
    }
  }, [periodFilter, statusFilter, methodFilter]);

  useEffect(() => {
    fetchPayments(periodFilter);
  }, [periodFilter, statusFilter, methodFilter, fetchPayments]);

  // Filtered Payments
  const filteredPayments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return payments.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (methodFilter !== "all" && p.paymentMethod !== methodFilter) return false;

      if (!q) return true;
      const cCode = String(p.companyCode || "").toLowerCase();
      const pId = String(p.razorpayPaymentId || "").toLowerCase();
      const oId = String(p.razorpayOrderId || "").toLowerCase();
      const vpa = String(p.vpa || "").toLowerCase();
      return cCode.includes(q) || pId.includes(q) || oId.includes(q) || vpa.includes(q);
    });
  }, [payments, searchQuery, statusFilter, methodFilter]);

  // Statistics
  const totalVolume = useMemo(() => {
    return payments
      .filter((p) => p.status === "paid")
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }, [payments]);

  const paidCount = useMemo(() => payments.filter((p) => p.status === "paid").length, [payments]);
  const pendingCount = useMemo(() => payments.filter((p) => p.status === "created" || p.status === "pending").length, [payments]);

  // Export CSV
  const handleExportCSV = () => {
    if (filteredPayments.length === 0) {
      message.warning("No payments to export.");
      return;
    }

    const headers = ["Company Code", "Amount (INR)", "Status", "Payment Method", "Razorpay Payment ID", "Razorpay Order ID", "Created At"];
    const rows = filteredPayments.map((p) => [
      `"${p.companyCode || ""}"`,
      p.amount || 0,
      `"${p.status || ""}"`,
      `"${p.paymentMethod || "N/A"}"`,
      `"${p.razorpayPaymentId || ""}"`,
      `"${p.razorpayOrderId || ""}"`,
      `"${p.createdAt ? new Date(p.createdAt).toISOString() : ""}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `DealVoice_Transactions_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    message.success("Transactions exported to CSV!");
  };

  const columns = [
    {
      title: "Company",
      dataIndex: "companyCode",
      key: "companyCode",
      width: 160,
      render: (code) => (
        <Space size={6}>
          <Text strong style={{ color: "#7c3aed", fontSize: "0.88rem" }}>
            {code || "—"}
          </Text>
          {onSelectCompany && (
            <Tooltip title="View Company Dossier">
              <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => onSelectCompany(code)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amt) => (
        <Text strong style={{ color: "#059669", fontSize: "0.95rem" }}>
          ₹{Number(amt || 0).toLocaleString("en-IN")}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (st) => (
        <Tag color={st === "paid" ? "success" : "warning"} style={{ borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>
          {st}
        </Tag>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 130,
      render: (m) => (
        <Tag style={{ textTransform: "uppercase", fontSize: "0.75rem", borderRadius: 4 }}>
          {m || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Transaction Identifiers",
      key: "ids",
      width: 260,
      render: (_, p) => (
        <Flex vertical gap={2} style={{ fontSize: "0.78rem" }}>
          {p.razorpayPaymentId && (
            <div>
              <Text type="secondary">Payment ID: </Text>
              <Text code style={{ color: "#7c3aed" }}>{p.razorpayPaymentId}</Text>
            </div>
          )}
          {p.razorpayOrderId && (
            <div>
              <Text type="secondary">Order ID: </Text>
              <Text code>{p.razorpayOrderId}</Text>
            </div>
          )}
          {(p.cardNetwork || p.cardLast4) && (
            <div>
              <Text type="secondary">Card: </Text>
              <span>{p.cardNetwork || "Card"} ending in <b>{p.cardLast4 || "****"}</b></span>
            </div>
          )}
          {p.vpa && (
            <div>
              <Text type="secondary">UPI: </Text>
              <span>{p.vpa}</span>
            </div>
          )}
        </Flex>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (dt) => (
        <Text style={{ fontSize: "0.82rem", color: "#475569" }}>
          {dt ? new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
        </Text>
      ),
    },
  ];

  return (
    <Spin spinning={loading} tip="Loading transactions...">
      <Flex vertical gap={20}>
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Flex vertical gap={2}>
          <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
            Global Transactions & Revenue Ledger
          </Title>
          <Text type="secondary" style={{ fontSize: "0.82rem" }}>
            Master billing and Razorpay payment records across all platform enterprises
          </Text>
        </Flex>

        <Flex align="center" gap={10}>
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV} style={{ borderRadius: 8 }}>
            Export CSV
          </Button>
          <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchPayments} disabled={loading} style={{ borderRadius: 8 }}>
            Refresh
          </Button>
        </Flex>
      </Flex>

      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card bordered={false} styles={{ body: { padding: "16px 20px" } }} style={{ borderRadius: 12, background: "#ffffff" }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: "0.8rem" }}>Total Revenue Volume</Text>}
              value={`₹${totalVolume.toLocaleString("en-IN")}`}
              valueStyle={{ fontSize: "1.4rem", fontWeight: 700, color: "#059669" }}
              prefix={<InrOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card bordered={false} styles={{ body: { padding: "16px 20px" } }} style={{ borderRadius: 12, background: "#ffffff" }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: "0.8rem" }}>Successful Transactions</Text>}
              value={paidCount}
              valueStyle={{ fontSize: "1.4rem", fontWeight: 700, color: "#2563eb" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card bordered={false} styles={{ body: { padding: "16px 20px" } }} style={{ borderRadius: 12, background: "#ffffff" }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: "0.8rem" }}>Pending Transactions</Text>}
              value={pendingCount}
              valueStyle={{ fontSize: "1.4rem", fontWeight: 700, color: "#f59e0b" }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Bar */}
      <Card bordered={false} styles={{ body: { padding: "14px 20px" } }} style={{ borderRadius: 12, background: "#ffffff" }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Input
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Search by company code, payment ID, order ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ width: 320, borderRadius: 8 }}
          />

          <Flex align="center" gap={12} wrap="wrap">
            <Flex align="center" gap={6}>
              <CalendarOutlined style={{ color: "#7c3aed", fontSize: "0.85rem" }} />
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>Period:</Text>
              <Select
                value={periodFilter}
                onChange={setPeriodFilter}
                style={{ width: 140 }}
                options={[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "this_week", label: "This Week" },
                  { value: "this_month", label: "This Month" },
                  { value: "last_month", label: "Last Month" },
                  { value: "this_quarter", label: "This Quarter" },
                  { value: "this_year", label: "This Year" },
                ]}
              />
            </Flex>

            <Flex align="center" gap={6}>
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>Status:</Text>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 120 }}
                options={[
                  { value: "all", label: "All Statuses" },
                  { value: "paid", label: "Paid" },
                  { value: "created", label: "Created" },
                  { value: "failed", label: "Failed" },
                ]}
              />
            </Flex>

            <Flex align="center" gap={6}>
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>Method:</Text>
              <Select
                value={methodFilter}
                onChange={setMethodFilter}
                style={{ width: 120 }}
                options={[
                  { value: "all", label: "All Methods" },
                  { value: "card", label: "Card" },
                  { value: "upi", label: "UPI" },
                  { value: "netbanking", label: "Netbanking" },
                  { value: "wallet", label: "Wallet" },
                ]}
              />
            </Flex>
          </Flex>
        </Flex>
      </Card>

      {/* Transactions Table */}
      <Card bordered={false} style={{ borderRadius: 14, background: "#ffffff" }}>
        <Table
          columns={columns}
          dataSource={filteredPayments}
          rowKey={(p, idx) => p._id || p.razorpayPaymentId || idx}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 880 }}
          size="middle"
          locale={{ emptyText: "No transactions found matching the filter criteria." }}
        />
      </Card>
    </Flex>
    </Spin>
  );
}

export default GlobalPaymentsView;
